import { useLocation, useNavigate } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (hash: string) => {
    const id = hash.replace('#', '');
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(scroll, 60);
    } else {
      scroll();
    }
  };

  const goToPageTop = (path: string) => {
    if (location.pathname !== path) navigate(path);
    window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
  };

  return (
    <footer className="relative overflow-hidden py-14 md:py-16 bg-[#027FDB] text-white border-t border-[#1D4ED8]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,rgba(219,234,254,0),rgba(219,234,254,0.55),rgba(219,234,254,0))]" />
        <div className="absolute -top-20 right-[-6rem] h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-[-5rem] left-[-4rem] h-64 w-64 rounded-full bg-[#60A5FA]/10 blur-3xl" />
      </div>
      <div className="relative z-10 max-w-[76rem] mx-auto px-5 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.35fr_0.8fr_0.95fr] gap-10 md:gap-8 lg:gap-10 text-center md:text-left">
          {/* Logo & Beschreibung */}
          <div className="flex flex-col items-center md:items-start">
            <button
              type="button"
              onClick={() => goToSection('#hero')}
              className="mb-4 flex items-center gap-3 justify-center md:justify-start"
            >
              <img
                src="/images/2.png"
                alt="Alltagsstern Logo"
                className="h-20 md:h-24 w-auto"
              />
            </button>
            <p className="text-[#DBEAFE]/78 mb-6 max-w-[34rem] text-[0.92rem] md:text-[0.98rem] leading-relaxed">
              Alltagsstern UG (haftungsbeschränkt) unterstützt in Berlin mit verlässlicher Haushaltshilfe und Alltagshilfe für Personen mit Pflegegrad. Von Wohnungsreinigung über Einkaufen bis zur Begleitung im Alltag.
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <a
                href="tel:+4915229990328"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-white/8 border border-white/10 shadow-[0_10px_24px_rgba(0,0,0,0.16)] hover:bg-white/12 hover:border-[#60A5FA]/40 transition-colors"
              >
                <i className="ri-phone-line text-[#DBEAFE] text-lg" />
              </a>
              <a
                href="mailto:info@alltagsstern.com"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-white/8 border border-white/10 shadow-[0_10px_24px_rgba(0,0,0,0.16)] hover:bg-white/12 hover:border-[#60A5FA]/40 transition-colors"
              >
                <i className="ri-mail-line text-[#DBEAFE] text-lg" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start lg:pl-2">
            <h4
              className="font-semibold text-[1rem] md:text-[1.08rem] mb-4 tracking-[0.03em]"
              style={{
                color: '#FFFFFF',
                fontFamily: 'Manrope, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
              }}
            >
              Schnellzugriff
            </h4>
            <ul className="space-y-2.5 text-[0.92rem] md:text-[0.96rem]">
              <li>
                <button type="button" onClick={() => goToSection('#hero')} className="text-[#DBEAFE]/80 hover:text-white transition-colors">
                  Start
                </button>
              </li>
              <li>
                <button type="button" onClick={() => goToSection('#services')} className="text-[#DBEAFE]/80 hover:text-white transition-colors">
                  Leistungen
                </button>
              </li>
              <li>
                <button type="button" onClick={() => goToSection('#process-section')} className="text-[#DBEAFE]/80 hover:text-white transition-colors">
                  Ablauf
                </button>
              </li>
              <li>
                <button type="button" onClick={() => goToSection('#kontakt')} className="text-[#DBEAFE]/80 hover:text-white transition-colors">
                  Kontakt
                </button>
              </li>
            </ul>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h4
              className="font-semibold text-[1rem] md:text-[1.08rem] mb-4 tracking-[0.03em]"
              style={{
                color: '#FFFFFF',
                fontFamily: 'Manrope, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
              }}
            >
              Kontakt
            </h4>
            <div className="space-y-3 text-[0.92rem] md:text-[0.96rem] w-full max-w-[19rem]">
              <a
                href="tel:+4915229990328"
                className="flex items-center justify-center md:justify-start gap-3 rounded-2xl border border-white/10 bg-white/[0.055] px-4 py-3 text-[#DBEAFE] hover:bg-white/[0.09] transition-colors"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/8 border border-white/10 shrink-0">
                  <i className="ri-phone-line text-[#DBEAFE] text-base" />
                </span>
                <span>+49 152 29990328</span>
              </a>
              <a
                href="mailto:info@alltagsstern.com"
                className="flex items-center justify-center md:justify-start gap-3 rounded-2xl border border-white/10 bg-white/[0.055] px-4 py-3 text-[#DBEAFE] hover:bg-white/[0.09] transition-colors"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/8 border border-white/10 shrink-0">
                  <i className="ri-mail-line text-[#DBEAFE] text-base" />
                </span>
                <span className="break-all">info@alltagsstern.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-[#DBEAFE]/60 text-xs md:text-sm text-center md:text-left">
            © 2026 Alltagsstern UG (haftungsbeschränkt). Alle Rechte vorbehalten.
          </p>
          <div className="flex gap-5 mt-1 md:mt-0 justify-center md:justify-end text-[0.8rem] md:text-[0.86rem]">
            <button type="button" onClick={() => goToPageTop('/impressum')} className="text-[#DBEAFE]/70 hover:text-white transition-colors">
              Impressum
            </button>
            <button type="button" onClick={() => goToPageTop('/datenschutz')} className="text-[#DBEAFE]/70 hover:text-white transition-colors">
              Datenschutz
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
